import React, { useState } from 'react';
import { Calculator, TrendingUp, ArrowRight, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import API_BASE_URL from '../config/api';

const MiniSimulatorRachat2P = () => {
  const [revenuAnnuel, setRevenuAnnuel] = useState(100000);
  const [lacune, setLacune] = useState(50000);
  const [montantRachat, setMontantRachat] = useState(30000);
  const [tauxImposition, setTauxImposition] = useState(25);
  const [result, setResult] = useState(null);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const calculateEconomie = () => {
    // Calcul de l'économie fiscale immédiate
    const economieImmediateFederale = montantRachat * 0.085; // Taux fédéral moyen
    const economieImmediate = montantRachat * (tauxImposition / 100);
    const economieTotal = economieImmediateFederale + economieImmediate;
    
    // Calcul de la rente supplémentaire (simplifié)
    const renteSupplementaire = montantRachat * 0.065; // 6.5% de conversion
    
    // Recommandation de stratégie
    let strategie = '';
    if (montantRachat > 50000) {
      strategie = 'Échelonné sur 3-5 ans';
    } else if (montantRachat > 25000) {
      strategie = 'Échelonné sur 2-3 ans ou unique';
    } else {
      strategie = 'Rachat unique possible';
    }
    
    setResult({
      economieImmediate,
      economieTotal,
      renteSupplementaire,
      strategie,
      tauxEconomie: ((economieTotal / montantRachat) * 100).toFixed(1)
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const handleSendPDF = async () => {
    if (!email || !result) return;
    
    setLoading(true);
    
    // Sauvegarder la position actuelle du scroll
    const scrollPosition = window.scrollY;
    
    try {
      const response = await fetch(`${API_BASE_URL}/lead-magnet`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          guide: 'Guide Rachat 2ème Pilier',
          position: 'simulator',
          simulator_data: {
            revenuAnnuel: revenuAnnuel,
            lacune: lacune,
            montantRachat: montantRachat,
            tauxImposition: tauxImposition,
            economieImmediateCantonale: result.economieImmediate,
            economieTotal: result.economieTotal,
            augmentationRente: result.renteSupplementaire,
            strategie: result.strategie
          }
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        
        // Restaurer la position du scroll
        setTimeout(() => {
          window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
        }, 0);
        
        setTimeout(() => setSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Error sending PDF:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass p-8 rounded-2xl border border-white/20 shadow-xl mb-8 bg-gradient-to-br from-green-500/10 to-emerald-500/10" id="simulateur">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-emerald-600 p-3 rounded-xl">
          <Calculator className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">
          📊 Calculez votre économie fiscale avec un rachat 2ème pilier
        </h3>
      </div>

      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Revenu annuel brut (CHF)
          </label>
          <input
            type="range"
            min="50000"
            max="300000"
            step="5000"
            value={revenuAnnuel}
            onChange={(e) => setRevenuAnnuel(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(revenuAnnuel)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Lacune de prévoyance estimée (CHF)
          </label>
          <input
            type="range"
            min="10000"
            max="200000"
            step="5000"
            value={lacune}
            onChange={(e) => setLacune(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(lacune)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Montant de rachat souhaité (CHF)
          </label>
          <input
            type="range"
            min="5000"
            max={lacune}
            step="1000"
            value={montantRachat}
            onChange={(e) => setMontantRachat(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {formatCurrency(montantRachat)}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Taux d'imposition marginal estimé (%)
          </label>
          <input
            type="range"
            min="10"
            max="45"
            step="1"
            value={tauxImposition}
            onChange={(e) => setTauxImposition(Number(e.target.value))}
            className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer"
          />
          <div className="text-right text-2xl font-bold text-white mt-2">
            {tauxImposition}%
          </div>
        </div>

        <button
          onClick={calculateEconomie}
          className="w-full bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-4 rounded-xl font-semibold hover:from-green-600 hover:to-emerald-700 transition-all transform hover:scale-105 flex items-center justify-center gap-2"
        >
          <Calculator className="w-5 h-5" />
          Calculer mon économie
        </button>

        {result && (
          <div className="mt-6 p-6 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-xl border border-green-500/30">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-6 h-6 text-green-400" />
              <h4 className="text-xl font-bold text-white">Votre économie potentielle</h4>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Économie fiscale totale :</span>
                <span className="text-2xl font-bold text-green-400">{formatCurrency(result.economieTotal)}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-300">Taux d'économie effective :</span>
                <span className="text-xl font-bold text-green-400">{result.tauxEconomie}%</span>
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-white/10">
                <span className="text-gray-300">Rente supplémentaire/an :</span>
                <span className="text-xl font-bold text-white">{formatCurrency(result.renteSupplementaire)}</span>
              </div>
              <div className="mt-4 p-4 bg-blue-500/20 rounded-lg border border-blue-500/30">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-blue-300 mb-1">Stratégie recommandée :</p>
                    <p className="text-white font-bold">{result.strategie}</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
              <p className="text-sm text-gray-300 mb-3">
                📧 Recevez votre rapport personnalisé par email
              </p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Votre email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-green-500"
                />
                <button
                  onClick={handleSendPDF}
                  disabled={!email || loading}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-2 rounded-lg font-semibold hover:from-green-600 hover:to-emerald-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {loading ? 'Envoi...' : 'Recevoir'}
                </button>
              </div>
              {success && (
                <p className="text-green-400 text-sm mt-2">✅ Rapport envoyé avec succès ! Consultez votre boîte mail.</p>
              )}
            </div>
            
            <Link
              to="/contact"
              className="mt-4 w-full bg-white text-green-600 px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
            >
              Optimiser mon rachat 2ème pilier
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniSimulatorRachat2P;